import { useEffect } from 'react';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { storeReturnAfterLogin } from '@utils/returnAfterLogin';

/**
 * Route layout for broker-authenticated pages. Handles:
 * - Unauthenticated → storeReturnAfterLogin + redirect to signup
 * - profileCompleted=false → redirect to signup-complete
 *
 * Renders <Outlet /> for children when all guards pass.
 * BrokerRoot (parent) already handles authLoading and isDashboardRole.
 */
export function ProtectedRoute() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { authLoading, isAuthenticated, profileCompleted, nafathVerified, userRole } = useAuth();

  const l = lang === 'ar' ? 'ar' : 'en';

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (profileCompleted === false) {
      navigate(`/${l}/signup-complete`, { replace: true });
      return;
    }
    if (userRole === 'user' && !nafathVerified) {
      navigate(`/${l}/nafath-verification`, { replace: true });
    }
  }, [authLoading, isAuthenticated, profileCompleted, nafathVerified, userRole, l, navigate]);

  if (!isAuthenticated) {
    if (typeof window !== 'undefined') {
      storeReturnAfterLogin(
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
    }
    return <Navigate to={`/${l}/signup`} replace />;
  }

  return <Outlet />;
}
